// @flow
import * as React from 'react';
import { Row, Col, Button } from 'react-bootstrap';
import { formatDate } from '../../helpers';
import fuzzyFilterFactory from 'react-fuzzy-filter';
import PaginatedList from '../../components/PaginatedList';
import ListFilterBox from '../../components/ListFilterBox';
import SelectDropdown from '../../components/SelectDropdown';
import LoadingSpinner from '../../components/LoadingSpinner';
import { CSSTransitionGroup } from 'react-transition-group';
import type { Industry, InsightSet } from '../../types';

const ControlHeader = props => (
  <Row className="tools">
    <Col sm={4} className="table-header">
      <ListFilterBox
        onChange={props.filterBoxChange}
        fuzzyFilterFactory={props.fuzzyFilterFactory}
      />
    </Col>

    <Col sm={3} smOffset={5} className="table-header">
      <Button
        bsStyle="primary"
        className="relative-padding"
        type="submit"
        onClick={props.addButtonClick}
      >
        Add Industry
      </Button>
    </Col>
  </Row>
);

const AddNewRow = props => (
  <Row>
    <Col sm={4} className="table-entry">
      <SelectDropdown
        onChange={props.selectOnChange}
        value={props.selectValue}
        options={props.items}
        aria-label="Industry Select Box"
        focus={true}
      />
    </Col>
    <Col sm={2} className="table-entry">
      <Button
        className="relative-padding"
        bsStyle="default"
        onClick={props.addItem}
      >
        Add
      </Button>
    </Col>
    <Col sm={2} className="table-entry">
      <Button
        bsStyle="link"
        className="relative-padding"
        onClick={props.cancel}
      >
        Cancel
      </Button>
    </Col>
  </Row>
);

const LineItem = props => {
  const industry = props.item;
  return (
    <Row key={industry.id}>
      <Col sm={4} className="table-entry">
        {industry.name}
      </Col>
      <Col sm={3} className="table-entry">
        {formatDate(industry.dateCreated, true)}
      </Col>
      <Col sm={3} className="table-entry">
        {formatDate(industry.dateUpdated, true)}
      </Col>
      <Col sm={2} className="table-entry">
        <Button
          className="relative-padding"
          bsStyle="link"
          onClick={() => props.removeItem(industry)}
          aria-label={`Remove ${industry.name}`}
        >
          Remove
        </Button>
      </Col>
    </Row>
  );
};

type Params = {
  insightSetId: string
};

type Props = {
  params: Params,
  currentInsightSet: InsightSet,
  getInsightSetIndustries: (
    i: number,
    a?: boolean,
    e?: boolean
  ) => Promise<void>,
  removeIndustryFromInsightSet: (id: number, i: Industry) => Promise<Response>,
  addIndustryToInsightSet: (isd: number, iid: number) => Promise<Response>
};

type State = {
  newIndustryId: ?number,
  showNewIndustry: boolean,
  filterValue: string,
  loading: boolean,
  isSaving?: boolean,
  errorMsg?: ?string,
  successMsg?: ?string
};

class InsightSetIndustries extends React.Component<Props, State> {
  fuzzyFilterFactory: any;
  timeoutId: any;
  screenReaderContent: any;
  static displayName = 'InsightSetIndustries';

  constructor(props: Props) {
    super(props);
    this.state = {
      newIndustryId: undefined,
      showNewIndustry: false,
      filterValue: '',
      loading: true
    };
    this.fuzzyFilterFactory = fuzzyFilterFactory();
    this.timeoutId = undefined;
  }

  componentDidMount() {
    const insightSetId = parseInt(this.props.params.insightSetId, 10);
    this.getIndustries(insightSetId);
    this.screenReaderContent.focus();
  }

  componentWillUnmount() {
    // clear timeout to prevent setState on unmounted component
    clearTimeout(this.timeoutId);
  }

  getIndustries = (insightSetId: number) => {
    // make API call to get industries for this InsightSet
    Promise.all([
      this.props.getInsightSetIndustries(insightSetId, true),
      this.props.getInsightSetIndustries(insightSetId, false)
    ]).then(() => this.setState({ loading: false }));
  };

  removeIndustry = (industry: Industry) => {
    this.setState({
      isSaving: true,
      errorMsg: undefined,
      successMsg: undefined
    });

    const insightSetId = parseInt(this.props.params.insightSetId, 10);
    this.props
      .removeIndustryFromInsightSet(insightSetId, industry)
      .then(response => this.success(response), error => this.failure(error));
  };

  addIndustry = () => {
    const newIndustryId = this.state.newIndustryId;
    if (newIndustryId) {
      this.setState({
        isSaving: true,
        errorMsg: undefined,
        successMsg: undefined
      });

      const insightSetId = parseInt(this.props.params.insightSetId, 10);
      this.props
        .addIndustryToInsightSet(insightSetId, newIndustryId)
        .then(response => this.success(response), error => this.failure(error));
    }
    this.setState({ showNewIndustry: false, newIndustryId: undefined });
  };

  success = (response: Response) => {
    this.setState({
      isSaving: false,
      errorMsg: undefined,
      successMsg: 'Success!'
    });
    this.timeoutId = setTimeout(
      () => this.setState({ successMsg: undefined }),
      3000
    );
  };

  failure = (error: Object) => {
    this.setState({ isSaving: false, errorMsg: error.data.message });
  };

  updateFilterValue = (filterValue: string) => {
    this.setState({ filterValue: filterValue || '' });
  };

  addButtonClick = () => {
    this.setState({ showNewIndustry: !this.state.showNewIndustry });
  };

  cancel = () => {
    this.setState({ showNewIndustry: false, newIndustryId: undefined });
  };

  selectOnChange = (newIndustryId: number) => {
    this.setState({ newIndustryId });
  };

  render() {
    const { allocated, unallocated } = this.props.currentInsightSet.industries;

    const LineItemWrapper = props => (
      <LineItem {...props} removeItem={this.removeIndustry} />
    );

    return (
      <div>
        <h3
          className="screen-reader-only"
          tabIndex="-1"
          ref={i => (this.screenReaderContent = i)}
        >
          Showing industries for insight set {this.props.currentInsightSet.name}
        </h3>

        <ControlHeader
          filterBoxChange={this.updateFilterValue}
          fuzzyFilterFactory={this.fuzzyFilterFactory}
          addButtonClick={this.addButtonClick}
        />

        <Row>
          <Col sm={4} className="table-header">
            Industry name
          </Col>
          <Col sm={3} className="table-header">
            Date created
          </Col>
          <Col sm={3} className="table-header">
            Date updated
          </Col>
          <Col sm={2} className="table-header">
            Action
          </Col>
        </Row>
        <hr />

        {this.state.showNewIndustry && (
          <AddNewRow
            items={unallocated.filter(i => !i.isArchived)}
            addItem={this.addIndustry}
            selectValue={this.state.newIndustryId}
            selectOnChange={this.selectOnChange}
            cancel={this.cancel}
          />
        )}
        {this.state.loading && <LoadingSpinner />}

        {!this.state.loading && (
          <PaginatedList
            filterValue={this.state.filterValue}
            component={LineItemWrapper}
            items={allocated}
            fuzzyFilterFactory={this.fuzzyFilterFactory}
            pluralName="industries"
          />
        )}

        <Row>
          <Col sm={12}>
            <CSSTransitionGroup
              transitionName="fade"
              transitionEnterTimeout={300}
              transitionLeaveTimeout={300}
            >
              {this.state.successMsg && (
                <p className="text-success">{this.state.successMsg}</p>
              )}
            </CSSTransitionGroup>
          </Col>
        </Row>
        {this.state.errorMsg && (
          <div className="alert alert-danger">{this.state.errorMsg}</div>
        )}
      </div>
    );
  }
}

export default InsightSetIndustries;



// WEBPACK FOOTER //
// ./src/containers/insightset/InsightSetIndustries.js